/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  LatLng,
  LatLngBounds,
  Layer,
  LayerGroup,
  Marker,
  markerClusterGroup,
  MarkerClusterGroupOptions,
  Polygon,
  Polyline,
  TileLayer,
  Map,
  FitBoundsOptions,
  Circle,
  Rectangle,
  CircleMarker,
  Point,
  Icon,
} from "leaflet";
import { PaintTypes } from "../style";
import { generateRandomHash } from "../../../core";
import { setHidroStyle } from "./calc_style";
import * as geojson from "geojson";

type Listener = (layers: HidroLayerGroup) => void;

export interface Actions {
  onClick?: (layer: Layer) => void;
  onDoubleClick?: (layer: Layer) => void;
  onMouseDown?: (layer: Layer) => void;
  onMouseUp?: (layer: Layer) => void;
  onMouseOver?: (layer: Layer) => void;
  onMouseOut?: (layer: Layer) => void;
  onMouseMove?: (layer: Layer) => void;
  onContextMenu?: (layer: Layer) => void;
  onPreClick?: (layer: Layer) => void;
}

export interface HidroLayerData {
  // Dados utilizaveis
  name: string;
  layer: Layer | LayerGroup<Polygon>;
  showing: boolean;

  // Estilos
  style?: PaintTypes;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: { [key: string]: any; };

  // Ações
  actions?: Actions;
}

export interface HidroLayersData {
  // Dados utilizaveis
  name: string;
  showing: boolean;

  // Dados das layers
  layers: (HidroLayer | HidroLayerGroup)[];

  // Para clusterizar os dados
  clusterize?: boolean;
  clusterizeOptions?: MarkerClusterGroupOptions;
}

export class HidroLayer {
  public name: string;
  public layer: Layer | LayerGroup<Polygon>;
  public style?: PaintTypes;
  public actions?: Actions;
  public properties?: { [key: string]: any; };

  private _showing: boolean;
  public get showing(): boolean {
    return this._showing;
  }
  public set showing(val: boolean) {
    if (val !== this._showing) {
      this._showing = val;
      if (this.onShowing) this.onShowing(val);
      if (this.mother && this.mother.onShowing) this.mother.onShowing(val);
      this.mother?.notify();
    }
  }

  public onShowing?: (show: boolean) => void;
  private mother?: HidroLayerGroup;

  constructor(data: HidroLayerData) {
    this.name = data.name;
    this._showing = data.showing ?? true;
    this.layer = data.layer;
    this.properties = data.properties;

    // Adicionando os estilos
    this.style = data.style;
    this.actions = data.actions;
    if (this.style) {
      setHidroStyle(this.layer, this.style, this.actions);
    }
  }

  // Bounds
  public getBounds(layer?: Layer): LatLngBounds | null {
    const lay: Layer | LayerGroup<Polygon> = layer ?? this.layer;
    if ("getBounds" in lay) return (lay.getBounds as () => LatLngBounds)();
    if ("getLatLng" in lay)
      return (lay.getLatLng as () => LatLng)().toBounds(0.1);
    return null;
  }

  public centerBounds(options?: FitBoundsOptions) {
    const map = this.mother?.map;
    const bounds = this.getBounds();
    if (map && bounds) {
      map.flyToBounds(bounds, options);
    }
  }

  // GeoJson
  public toGeoJson(): geojson.Feature | false {
    let layer = this.layer;

    if (layer instanceof Circle) layer = circleToPolygon(layer);

    if ("toGeoJSON" in layer) {
      const geojson = (layer.toGeoJSON as () => geojson.Feature)();
      geojson.properties = this.properties ?? {};
      geojson.bbox = [
        this.getBounds(layer)?.getSouthWest().lng ?? 0,
        this.getBounds(layer)?.getSouthWest().lat ?? 0,
        this.getBounds(layer)?.getNorthEast().lng ?? 0,
        this.getBounds(layer)?.getNorthEast().lat ?? 0,
      ];
      return geojson;
    }

    return false;
  }

  // HidroLayerGroup mãe
  public setMother(mother: HidroLayerGroup) {
    this.mother = mother;
  }

  public getMother(): HidroLayerGroup | undefined {
    return this.mother;
  }

  // Style
  public setStyle(newStyle: PaintTypes) {
    this.style = newStyle;
    if (this.style) setHidroStyle(this.layer, this.style, this.actions);
    // this.mother?.notify(); TODO: Testar, acredito que não precise notificar os listeners para alterar o estilo
  }

  public setActions(newActions: Actions) {
    this.actions = newActions;
    setHidroStyle(this.layer, this.style ?? {}, this.actions);
    // this.mother?.notify(); TODO: Testar, acredito que não precise notificar os listeners para alterar o estilo
  }
}

// TODO: Melhorar o desempenho ao ativar, desativar ou mover as layers
export class HidroLayerGroup {
  public name: string;
  public layers: (HidroLayer | HidroLayerGroup)[];
  public clusterize?: boolean;
  public clusterizeOptions?: MarkerClusterGroupOptions;

  // Timer notify
  private timerNotify?: NodeJS.Timeout;

  constructor(data: HidroLayersData) {
    this.name = data.name;
    this._showing = data.showing ?? true;
    this.clusterize = data.clusterize;
    this.clusterizeOptions = data.clusterizeOptions;

    data.layers.forEach((layer) => {
      layer.setMother(this);
    });
    this.layers = data.layers;
  }

  // current map
  private _map?: Map;
  public get map(): Map | undefined {
    return this._map ?? this.mother?.map;
  }
  public set map(mapa: Map | undefined) {
    this._map = mapa;
    if (this.mother) {
      this.mother.map = mapa;
    }
    this.notify();
  }
  public setMap(mapa: Map | undefined) {
    this.map = mapa;
  }

  // Controles do show da layer
  private _showing: boolean;
  public get showing(): boolean | undefined {
    let active = this.layers.length > 0 ? false : true;
    let inactive = false;
    this.layers.forEach((layer) => {
      if (layer.showing === true || layer.showing === undefined) active = true;
      if (layer.showing === false) inactive = true;
    });

    if (this._showing && !inactive && active) return true; // Todas ativas
    if (this._showing && inactive && active) return undefined; // Ativas e inativas
    if (this._showing && inactive && !active) return false; // Todas inativas

    return false; // Caso este grupo não deva ser mostrado independente dos items que ele contém
  }
  public set showing(show: boolean | undefined) {
    this._showing = show ?? false;
    if (this.onShowing) this.onShowing(show ?? false);
    if (this.mother && this.mother.onShowing)
      this.mother.onShowing(show ?? false);
    if (this.mother) this.mother.notify();
    this.notify();
  }
  public showAll(show: boolean) {
    this.layers.forEach((layer) => {
      if (layer instanceof HidroLayer) {
        layer.showing = show;
      } else {
        layer.showAll(show);
      }
    });
    this.showing = show;
  }

  // Funções de estado
  public onMoveLayer?: (oldIndex: number, newIndex: number) => void;
  public onShowing?: (show: boolean) => void;
  private mother?: HidroLayerGroup;

  // BBOX
  public getBounds(ignoreShowing = false): LatLngBounds | null {
    const bounds = this.layers
      .filter((layer) => ignoreShowing || layer.showing !== false)
      .map((layer) => layer.getBounds())
      .filter((bounds) => bounds !== null);

    if (!bounds.length) return null;

    const allBounds = bounds[0] as LatLngBounds;
    bounds.forEach((bound) => (bound ? allBounds.extend(bound) : null));
    return allBounds;
  }

  public centerBounds(options?: FitBoundsOptions) {
    const map = this.mother?.map ?? this.map;
    const bounds = this.getBounds();
    if (map && bounds) {
      map.flyToBounds(
        bounds,
        options ?? {
          padding: [3, 3],
        }
      );
    }
  }

  // To GeoJson
  public toGeoJson(
    layers?: (HidroLayer | HidroLayerGroup)[]
  ): geojson.FeatureCollection {
    const layersNow = layers ?? this.layers;

    const collection: geojson.FeatureCollection = {
      type: "FeatureCollection",
      features: [],
    };

    for (const layer of layersNow) {
      if (layer instanceof HidroLayerGroup) {
        for (const feature of this.toGeoJson(layer.layers).features) {
          collection.features.push(feature);
        }
        continue;
      }

      const jsonData = layer.toGeoJson();
      if (jsonData) collection.features.push(jsonData);
    }

    return collection;
  }

  // Atualização de estado
  public setMother(mother: HidroLayerGroup) {
    this.mother = mother;
  }

  public getMother(): HidroLayerGroup | undefined {
    return this.mother;
  }

  private listeners: { [key: string]: Listener; } = {};
  public addListener(listener: Listener) {
    let key = generateRandomHash(8);
    while (key in this.listeners) key = generateRandomHash(8);

    this.listeners[key] = listener;

    return key;
  }
  public removeListener(key: string): void {
    if (this.listeners[key]) delete this.listeners[key];
  }
  public notify() {
    if (this.timerNotify) clearTimeout(this.timerNotify);

    this.timerNotify = setTimeout(() => {
      for (const key in this.listeners) {
        this.listeners[key](this);
      }

      if (this.mother) this.mother.notify();
    }, 100);
  }

  // Ações com as layers
  public flat(layers: (HidroLayer | HidroLayerGroup)[]) {
    layers.forEach((layer) => {
      layer.setMother(this);
      layer.onShowing = this.onShowing;

      this.layers.push(layer);
    });

    // Notificar os listners
    this.notify();
  }

  public push(layer: HidroLayer | HidroLayerGroup) {
    layer.setMother(this);
    layer.onShowing = this.onShowing;

    this.layers.push(layer);

    // Notificar os listners
    this.notify();
  }

  public filter(
    filter: ((layer: HidroLayer) => boolean) | "Polygon" | "Polyline" | "Point"
  ): (HidroLayer | HidroLayerGroup)[] {
    const layersReturn: (HidroLayer | HidroLayerGroup)[] = [];

    for (const layer of this.layers) {
      if (layer instanceof HidroLayerGroup) {
        const layersAdd = layer.filter(filter);
        if (layersAdd.length > 0) {
          layer.clearLayers();
          layer.flat(layersAdd);
          layersReturn.push(layer);
        }
        continue;
      }

      if (
        !(filter === "Point" || filter === "Polygon" || filter === "Polyline")
      ) {
        const add = filter(layer);
        if (add) {
          layersReturn.push(layer);
        }
        continue;
      }

      let isGeomRequested = false;
      if (filter === "Point") {
        isGeomRequested =
          layer.layer instanceof Point ||
          layer.layer instanceof Marker ||
          layer.layer instanceof Icon ||
          layer.layer instanceof CircleMarker;
      } else if (filter === "Polygon") {
        isGeomRequested =
          layer.layer instanceof Polygon ||
          layer.layer instanceof Rectangle ||
          layer.layer instanceof Circle;
      } else if (filter === "Polyline") {
        isGeomRequested = layer.layer instanceof Polyline;
      }

      isGeomRequested = isGeomRequested || layer.layer instanceof TileLayer;

      if (isGeomRequested) {
        layersReturn.push(layer);
        continue;
      }
    }
    return layersReturn;
  }

  public moveLayer(oldIndex: number, newIndex: number) {
    // Verifica se os índices são válidos
    if (
      oldIndex < 0 ||
      oldIndex >= this.layers.length ||
      newIndex < 0 ||
      newIndex >= this.layers.length
    ) {
      throw new Error("Índices inválidos");
    }

    // Remove o item do índice antigo
    const [item] = this.layers.splice(oldIndex, 1);

    // Insere o item no novo índice
    this.layers.splice(newIndex, 0, item);

    if (this.onMoveLayer) this.onMoveLayer(oldIndex, newIndex);

    // Notificar os listners
    this.notify();

    return this;
  }

  public removeLayer(layer: HidroLayer | HidroLayerGroup | Layer) {
    if (layer instanceof Layer) {
      return this._removeLayerUsingLayer(layer);
    }

    const index = this.layers.indexOf(layer);
    if (index > -1) {
      this.layers.splice(index, 1);
      this.notify();
    }
  }

  public clearLayers(notify = true) {
    while (this.layers.length) this.layers.pop();
    if (notify) this.notify();
  }

  public setActions(actions: Actions) {
    this.layers.forEach((layer) => {
      layer.setActions(actions);
    });
  }

  public getHidrolayerByLayer(
    layerFind: Layer | ((layer: HidroLayer) => boolean),
    layers?: (HidroLayer | HidroLayerGroup)[]
  ): HidroLayer | undefined {
    const layersNow = layers ?? this.layers;

    for (const layer of layersNow) {
      if (layer instanceof HidroLayerGroup) {
        const lay = this.getHidrolayerByLayer(layerFind, layer.layers);
        if (lay) return lay;
        continue;
      }
      if (layerFind instanceof Layer && layer.layer === layerFind) {
        return layer;
      } else if (!(layerFind instanceof Layer) && layerFind(layer)) {
        return layer;
      }
    }

    return undefined;
  }

  // Cópias
  public copy(): HidroLayerGroup {
    return new HidroLayerGroup({
      name: this.name,
      showing: this.showing ?? false,
      layers: this.cloneLayers(),
      clusterize: this.clusterize,
      clusterizeOptions: this.clusterizeOptions,
    });
  }

  // Obter as layers
  public cloneLayers() {
    return this.layers.map((layer) => {
      if (layer instanceof HidroLayer)
        return new HidroLayer({
          name: layer.name,
          showing: layer.showing,
          layer: this.cloneLayer(layer.layer),
          properties: { ...layer.properties },
        });

      return layer.copy();
    });
  }

  public cloneLayer(layer: Layer | LayerGroup): Layer | LayerGroup {
    if (layer instanceof TileLayer) {
      return new TileLayer((layer as any)._url, layer.options);
    } else if (layer instanceof Marker) {
      return new Marker(layer.getLatLng(), layer.options);
    } else if (layer instanceof Circle) {
      return new Circle(layer.getLatLng(), layer.options);
    } else if (layer instanceof Rectangle) {
      return new Rectangle(layer.getBounds(), layer.options);
    } else if (layer instanceof Polygon) {
      return new Polygon(layer.getLatLngs(), layer.options);
    } else if (layer instanceof Polyline) {
      return new Polyline(layer.getLatLngs() as LatLng[], layer.options);
    } else if (layer instanceof LayerGroup) {
      return new LayerGroup(layer.getLayers().map(this.cloneLayer)); // Recursividade para obter as layers internas no multilayer
    } else if (layer && typeof (layer as any).clone === "function") {
      return (layer as any).clone() as Layer;
    }
    throw Error("Tipo de layer inválido");
  }

  public getLayers(): LayerGroup {
    return this._getLayers(this.layers);
  }

  public getTiles(): LayerGroup {
    return this._getTiles(this.layers);
  }

  // Funções privadas
  private _getLayers(layers: (HidroLayer | HidroLayerGroup)[]): LayerGroup {
    const layersNow = layers
      .filter((layer) => layer.showing !== false)
      .flatMap((layer) => {
        if (layer instanceof HidroLayerGroup) {
          const layerGroup = this._getLayers(layer.layers);
          if (!layer.clusterize) return layerGroup;

          const clusterOpt = layer.clusterizeOptions;
          const cluster = markerClusterGroup(clusterOpt);
          layerGroup.eachLayer((layer) => cluster.addLayer(layer));

          return cluster;
        }
        return layer.layer;
      });

    return new LayerGroup(layersNow);
  }

  private _getTiles(layers: (HidroLayer | HidroLayerGroup)[]): LayerGroup {
    const layersNow = layers
      .filter((layer) => {
        if ("layers" in layer) return true;
        if (layer.layer instanceof TileLayer) return true;
        return false;
      })
      .flatMap((layer) => {
        if (layer instanceof HidroLayerGroup) {
          const layerGroup = this._getTiles(layer.layers);
          if (!layer.clusterize) return layerGroup;

          const clusterOpt = layer.clusterizeOptions;
          const cluster = markerClusterGroup(clusterOpt);
          layerGroup.eachLayer((layer) => cluster.addLayer(layer));

          return cluster;
        }
        return layer.layer;
      });

    return new LayerGroup(layersNow);
  }

  private _removeLayerUsingLayer(
    layer: Layer,
    hidroLayers?: HidroLayerGroup
  ): boolean {
    const layerGroup = hidroLayers ?? this;

    for (const hidro of layerGroup.layers) {
      if (hidro instanceof HidroLayerGroup) {
        const removed = this._removeLayerUsingLayer(layer, hidro);
        if (removed && hidro.layers.length === 0) {
          layerGroup.removeLayer(hidro);
        }

        if (removed) {
          return removed;
        }

        continue;
      }

      if (hidro.layer === layer) {
        layerGroup.removeLayer(hidro);
        return true;
      }
    }
    return false;
  }
}

export const hidroLayer = (data: HidroLayerData) => {
  return new HidroLayer(data);
};
export const hidroLayerGroup = (data: HidroLayersData) => {
  return new HidroLayerGroup(data);
};

// Circulo para polígono
function circleToPolygon(circle: Circle, points = 50): Polygon {
  const center = circle.getLatLng();
  const radius = circle.getRadius();

  const latlngs: L.LatLng[] = [];
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI;
    const dx = radius * Math.cos(angle);
    const dy = radius * Math.sin(angle);

    // Calcula o ponto no círculo (usando projeção planar simples para exemplo)
    const point = new LatLng(
      center.lat + (180 / Math.PI) * (dy / 6378137),
      center.lng +
        ((180 / Math.PI) * (dx / 6378137)) /
          Math.cos((Math.PI / 180) * center.lat)
    );
    latlngs.push(point);
  }

  // Fecha o polígono (último ponto = primeiro ponto)
  latlngs.push(latlngs[0].clone());

  return new Polygon(latlngs);
}
